import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  School, Users, BookOpen, CheckCircle, Loader2, Phone, Mail, MapPin, Percent, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useSiteCustomization } from "@/context/SiteCustomizationContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  "Greater Accra", "Ashanti", "Western", "Eastern", "Central", "Northern",
  "Volta", "Upper East", "Upper West", "Bono", "Bono East", "Ahafo",
  "Savannah", "North East", "Western North", "Oti",
];

const ICON_MAP = {
  percent: Percent, school: School, clipboard: CheckCircle, users: Users, book: BookOpen,
};

export default function SchoolServicesPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refNumber, setRefNumber] = useState("");
  const [config, setConfig] = useState(null);
  const { config: siteConfig } = useSiteCustomization();
  const schoolBg = siteConfig?.images?.schoolServicesBg || "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/smq7dle8_image.png";
  const schoolPhoto = siteConfig?.images?.schoolServicesPhoto || "https://customer-assets.emergentagent.com/job_bf28faf8-971e-4f4e-94e5-2e197ca6b6dd/artifacts/7m2f21ff_WhatsApp%20Image%202026-02-09%20at%209.46.57%20PM.jpeg";
  const [formData, setFormData] = useState({
    school_name: "", ges_registration_number: "", contact_person: "",
    email: "", phone: "", region: "", subjects: [], class_levels: [],
    quantity: "", format_preference: "", school_branded: false,
    custom_assessment: false, additional_notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/school-config`).then(res => setConfig(res.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleSubject = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject) ? prev.subjects.filter(s => s !== subject) : [...prev.subjects, subject],
    }));
  };

  const toggleClassLevel = (level) => {
    setFormData(prev => ({
      ...prev,
      class_levels: prev.class_levels.includes(level) ? prev.class_levels.filter(l => l !== level) : [...prev.class_levels, level],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.subjects.length === 0) { toast.error("Please select at least one subject"); return; }
    if (formData.class_levels.length === 0) { toast.error("Please select at least one class level"); return; }

    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.quantity) payload.quantity = parseInt(payload.quantity);
      const res = await axios.post(`${API}/inquiries`, payload);
      setRefNumber(res.data.reference_number || "");
      setSubmitted(true);
      toast.success("Inquiry submitted successfully!");
    } catch {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isDigital = formData.format_preference === "Digital (PDF)";
  const subjects = config?.subjects || [];
  const classLevels = config?.class_levels || [];
  const features = config?.features || [];
  const pricingTiers = config?.pricing_tiers || [];
  const specialRequests = config?.special_requests || [];
  const formatOptions = config?.format_options || ["Digital (PDF)", "Physical (Printed)", "Both"];
  const quantityTooltip = config?.quantity_tooltip || "Enter the number of printed copies needed per exam subject.";

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="text-center">
            <CardContent className="pt-12 pb-8">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-success" />
              </div>
              <h1 className="font-heading text-3xl font-bold mb-4">Inquiry Submitted!</h1>
              {refNumber && (
                <div className="mb-4 inline-block bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2">
                  <p className="text-xs text-emerald-600 font-medium mb-1">Your Reference Number</p>
                  <p className="text-lg font-bold text-emerald-700 tracking-wide">{refNumber}</p>
                </div>
              )}
              <p className="text-muted-foreground mb-8">
                Thank you for your interest in Great Messiah Publication. Our team will review your request
                and contact you within 24-48 hours with a customized quote.{refNumber && " A confirmation email has been sent to you."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => setSubmitted(false)} variant="outline">Submit Another Inquiry</Button>
                <Button asChild className="bg-primary">
                  <a href="https://wa.me/233501234567" target="_blank" rel="noopener noreferrer">
                    <Phone className="w-4 h-4 mr-2" />Contact Us on WhatsApp
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-navy-900 py-16 lg:py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url('${schoolBg}')` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/" className="text-gray-300 hover:text-white">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-gray-500" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">For Schools</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6" data-testid="school-hero-title">
                {config?.hero_title || "Bulk Mock Exams for Schools"}
              </h1>
              <p className="text-lg text-gray-300 mb-8" data-testid="school-hero-desc">
                {config?.hero_description || "Partner with Great Messiah Publication for high-quality, GES-aligned mock examinations at special bulk pricing. We offer custom branding and tailored assessments for your institution."}
              </p>

              {/* Features */}
              <div className="grid sm:grid-cols-2 gap-4" data-testid="school-features">
                {features.length > 0 ? features.map((feature, i) => {
                  const Icon = ICON_MAP[feature.icon] || CheckCircle;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-white/80" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{feature.title}</p>
                        <p className="text-sm text-gray-400">{feature.description}</p>
                      </div>
                    </div>
                  );
                }) : (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                        <Percent className="w-5 h-5 text-white/80" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Bulk Discounts</p>
                        <p className="text-sm text-gray-400">Up to 35% off for large orders</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                        <School className="w-5 h-5 text-white/80" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Custom Branding</p>
                        <p className="text-sm text-gray-400">Add your school logo</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                        <BookOpen className="w-5 h-5 text-white/80" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Custom Assessments</p>
                        <p className="text-sm text-gray-400">Tailored to your needs</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                        <Users className="w-5 h-5 text-white/80" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Dedicated Support</p>
                        <p className="text-sm text-gray-400">Priority assistance</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="hidden lg:block">
              <img
                src={schoolPhoto}
                alt="Students in green uniforms studying in classroom"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-center mb-4">Bulk Pricing</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            The more you order, the more you save. All prices are per individual exam copy.
          </p>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto" data-testid="school-pricing">
            {pricingTiers.map((tier, i) => (
              <Card key={i} className={`relative overflow-hidden ${tier.popular ? 'border-primary shadow-lg scale-105' : 'hover:shadow-md'} transition-all`}>
                {tier.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-primary text-white text-center text-xs font-bold py-1">MOST POPULAR</div>
                )}
                <CardContent className={`text-center ${tier.popular ? 'pt-10' : 'pt-8'} pb-8`}>
                  <div className="text-4xl font-bold text-primary mb-1">GH&#8373; {tier.price}</div>
                  <div className="text-sm text-muted-foreground mb-4">{tier.label}</div>
                  <div className="font-medium mb-2">{tier.range}</div>
                  <div className="text-sm text-success font-medium">{tier.discount}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquiry-form" className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">Request a Quote</CardTitle>
              <CardDescription>Fill out the form below and we'll get back to you within 24-48 hours with a customized quote.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* School Information */}
                <div className="space-y-4">
                  <h3 className="font-medium border-b pb-2">School Information</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="school_name">School Name *</Label>
                      <Input id="school_name" name="school_name" value={formData.school_name} onChange={handleChange} placeholder="Enter school name" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ges_registration_number">GES Registration Number</Label>
                      <Input id="ges_registration_number" name="ges_registration_number" value={formData.ges_registration_number} onChange={handleChange} placeholder="Optional" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact_person">Contact Person *</Label>
                      <Input id="contact_person" name="contact_person" value={formData.contact_person} onChange={handleChange} placeholder="Full name" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="region">Region *</Label>
                      <Select value={formData.region} onValueChange={(v) => setFormData(prev => ({ ...prev, region: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select region" /></SelectTrigger>
                        <SelectContent>{REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="school@email.com" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="0501234567" required />
                    </div>
                  </div>
                </div>

                {/* Order Details */}
                <div className="space-y-4">
                  <h3 className="font-medium border-b pb-2">Order Details</h3>

                  <div className="space-y-2">
                    <Label>Subjects Required *</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {subjects.map(subject => (
                        <div key={subject} className="flex items-center gap-2">
                          <Checkbox id={`subject-${subject}`} checked={formData.subjects.includes(subject)} onCheckedChange={() => toggleSubject(subject)} />
                          <label htmlFor={`subject-${subject}`} className="text-sm cursor-pointer">{subject}</label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Class Levels *</Label>
                    <div className="flex flex-wrap gap-4">
                      {classLevels.map(level => (
                        <div key={level} className="flex items-center gap-2">
                          <Checkbox id={`level-${level}`} checked={formData.class_levels.includes(level)} onCheckedChange={() => toggleClassLevel(level)} />
                          <label htmlFor={`level-${level}`} className="text-sm cursor-pointer">{level}</label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="format_preference">Format Preference</Label>
                      <Select value={formData.format_preference} onValueChange={(v) => setFormData(prev => ({ ...prev, format_preference: v, ...(v === "Digital (PDF)" ? { quantity: "" } : {}) }))}>
                        <SelectTrigger><SelectValue placeholder="Select format" /></SelectTrigger>
                        <SelectContent>
                          {formatOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {!isDigital && (
                      <div className="space-y-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Label htmlFor="quantity" className="inline-flex items-center gap-1 cursor-help">
                                Total Quantity (per exam) {!isDigital && "*"}
                                <HelpCircle className="w-3.5 h-3.5 text-muted-foreground" />
                              </Label>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-sm">
                              <p>{quantityTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Input
                          id="quantity" name="quantity" type="number" value={formData.quantity}
                          onChange={handleChange} placeholder="e.g., 100" min="50"
                          required={!isDigital}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Special Requests */}
                <div className="space-y-4">
                  <h3 className="font-medium border-b pb-2">Special Requests</h3>
                  {specialRequests.map((sr) => (
                    <div key={sr.key} className="flex items-center gap-2">
                      <Checkbox
                        id={sr.key}
                        checked={formData[sr.key] || false}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, [sr.key]: checked }))}
                      />
                      <label htmlFor={sr.key} className="text-sm cursor-pointer">{sr.label}</label>
                    </div>
                  ))}
                  <div className="space-y-2">
                    <Label htmlFor="additional_notes">Additional Notes</Label>
                    <Textarea id="additional_notes" name="additional_notes" value={formData.additional_notes} onChange={handleChange} placeholder="Any specific requirements or questions..." rows={4} />
                  </div>
                </div>

                <Button type="submit" className="w-full h-12 bg-primary hover:bg-primary/90" disabled={loading}>
                  {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting...</>) : "Submit Inquiry"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
